import { stringToColour } from "../../../../../app/utils/changeBackgroundColorOfInitials";
import { formatMoneyValue } from "../../../../../app/utils/formatMoneyValue";
import { getNameInitial } from "../../../../../app/utils/getNameInitial";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../../../components/ui/avatar";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "../../../../components/ui/card";
import { Team } from "../../../../../app/entities/Team";
import { RemoveTeam } from "./RemoveTeam";
import { formatName } from "../../../../../app/utils/formatName";
import { BasicAvatar } from "../../../../components/BasicAvatar";
import { SyntheticEvent, useMemo } from "react";
import { useToast } from "@/view/components/ui/use-toast";
import { useAuth } from "@/app/hooks/useAuth";
import { UserRoleEnum } from "@/app/services/usersService/user";

const TEAMS_URL = import.meta.env.VITE_TEAMS_URL;

interface ExecutiveViewProps {
  name?: string;
  profileImageUrl?: string;
  role: UserRoleEnum.LEADER | UserRoleEnum.MANAGER | UserRoleEnum.SUPERVISOR;
}
const ExecutiveView = ({ role, name, profileImageUrl }: ExecutiveViewProps) => {
  const config = useMemo(() => {
    const dicionary = {
      [UserRoleEnum.MANAGER]: {
        label: "Gerente",
        color: "#288F63",
        bg: "#E3F9EC",
      },
      [UserRoleEnum.SUPERVISOR]: {
        label: "Supervisor",
        color: "#A68E38",
        bg: "#FDF3D0",
      },
      [UserRoleEnum.LEADER]: {
        label: "Líder",
        color: "#C35132",
        bg: "#FFF4ED",
      },
    };

    return dicionary[role];
  }, [role]);

  return (
    <div className="my-2 flex w-full flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-3">
        <BasicAvatar name={name} profileImageUrl={profileImageUrl} />
        <p className="text-sm font-semibold">{name ? formatName(name) : ""}</p>
      </div>
      <div className="flex items-center gap-2">
        <div
          className={"h-fit w-fit rounded-3xl px-2 py-1"}
          style={{ background: config.bg }}
        >
          <p
            className={"text-xs font-semibold "}
            style={{ color: config.color }}
          >
            {config?.label}
          </p>
        </div>
      </div>
    </div>
  );
};

export interface TeamsCardProps extends Team {
  handleGoToTeamsPage: (teamsId: string) => void;
}

export const TeamsCard = ({
  handleGoToTeamsPage,
  _id,
  code,
  leader,
  manager,
  supervisor,
  members_count: memberCount,
  members,
  name,
  transactions_count: transactionsCount,
  transactions_total: transactionsTotal,
}: TeamsCardProps) => {
  const { toast } = useToast();
  const { isSuperAdmin } = useAuth();

  const teamUrl = `${TEAMS_URL}${code}`;

  const copyTeamLink = async (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const type = "text/plain";
    const blob = new Blob([teamUrl], { type });
    const data = [new ClipboardItem({ [type]: blob })];
    await navigator.clipboard.write(data);

    toast({ variant: "default", title: "Link copiado" });
  };

  const noTeam = _id === "semTime";
  return (
    <Card
      onClick={() => handleGoToTeamsPage(_id)}
      className="group relative flex min-h-[480px] cursor-pointer flex-col"
    >
      <CardHeader className="grow">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-semibold">{name}</h3>
          {isSuperAdmin && !noTeam && (
            <div className="hidden group-hover:block">
              <RemoveTeam id={_id} />
            </div>
          )}
        </div>
        {code && (
          <p className="!mb-2 max-w-full overflow-hidden text-ellipsis text-sm font-medium text-blue-600">
            {teamUrl}
          </p>
        )}
        {/* {!noTeam && goal.length > 1 && (
          <>
            <div className="flex items-center gap-2">
              <p className="font-semibold">Bonificação</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info size={16} color="black" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Detalhes sobre a comissão</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Goals currentValue={transactionsTotal} goals={goal} />
          </>
        )} */}
      </CardHeader>
      <CardContent>
        {!noTeam && (
          <>
            {manager?.name && (
              <ExecutiveView
                role={UserRoleEnum.MANAGER}
                name={manager?.name}
                profileImageUrl={manager?.profileImageUrl}
              />
            )}

            {supervisor?.name && (
              <ExecutiveView
                role={UserRoleEnum.SUPERVISOR}
                name={supervisor?.name}
                profileImageUrl={supervisor?.profileImageUrl}
              />
            )}

            {leader?.name && (
              <ExecutiveView
                role={UserRoleEnum.LEADER}
                name={leader?.name}
                profileImageUrl={leader?.profileImageUrl}
              />
            )}
          </>
        )}
        <div className="mt-4 flex items-center justify-center gap-2">
          <div className="flex h-20 w-full flex-col items-center justify-center rounded-lg border-[1px] border-gray02 bg-white02 px-3 py-2">
            <p className="font-semibold">{(transactionsCount || 0).toLocaleString('pt-BR')}</p>
            <p className="text-xs font-medium text-black04">
              Saques realizados
            </p>
          </div>
          <div className="flex h-20 w-full flex-col items-center justify-center rounded-lg border-[1px] border-gray02 bg-white02 px-3 py-2">
            <p className="font-semibold">
              {formatMoneyValue(transactionsTotal)}
            </p>
            <p className="text-xs font-medium text-black04">Movimentado</p>
          </div>
        </div>
        <div className="mt-4 w-full border-[1px] border-white02" />
      </CardContent>
      <CardFooter className="flex flex-col items-start">
        <div className="flex w-full items-center justify-between">
          <h3 className="text-sm font-semibold">
            {(memberCount || 0).toLocaleString('pt-BR')} {memberCount === 1 ? "membro" : "membros"}
          </h3>
          {code && (
            <div
              className="cursor-pointer rounded bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
              onClick={copyTeamLink}
            >
              Copiar link
            </div>
          )}
        </div>
        <div className="mt-4 flex min-h-4 items-center">
          {members.length > 0 ? (
            <>
              {members.slice(0, 10).map((member, index) => (
                <Avatar
                  key={member._id}
                  className="h-8 w-8 border-2 border-white"
                  style={{ marginLeft: index > 0 ? "-6px" : "0" }}
                >
                  <AvatarImage
                    src={`${member?.profileImageUrl ? `${import.meta.env.VITE_CDN_URL}${member.profileImageUrl}` : ""}`}
                  />
                  <AvatarFallback
                    style={{
                      backgroundColor: `${stringToColour(member?.name)}`,
                    }}
                    className="text-sm"
                  >
                    {getNameInitial(member?.name ?? "")}
                  </AvatarFallback>
                </Avatar>
              ))}
              {members.length > 10 && (
                <>
                  <div
                    style={{
                      backgroundColor: `${stringToColour("Henrique novais")}`,
                    }}
                    className="-ml-[4px] h-8 w-8 rounded-full border-2 border-white"
                  >
                    <div className="ml-2">+{members.length - 10}</div>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="p-4 text-sm text-black04" />
          )}
        </div>
      </CardFooter>
    </Card>
  );
};
